import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Edit, Phone, Eye, Calendar, Truck, CheckCircle, Clock, Package, PauseCircle, RotateCcw, XCircle, User, Copy, Check } from 'lucide-react';
import { PageHeader, DataTable, FilterPanel, StatsCards, MultiSelectFilter, ExportProgressModal } from '../../components/common';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getShiftDetails, getShiftCrossSellItems } from '../../services/api/callCenterShifts';
import { getEcomOrder } from '../../services/api/orders';
import { getUsers } from '../../services/api/users';
import { formatDate as formatOrderDate, formatTime, formatDateTime } from '../../utils/dateUtils';
import { exportRowsToExcel } from '../../utils/excelExport';
import CallCenterShiftModal from './components/CallCenterShiftModal';
import StaffSlabBreakdownModal from './components/StaffSlabBreakdownModal';
import OrderDetailsModal from '../orders/components/OrderDetailsModal';
import AirwaybillTrackingModal from '../orders/components/AirwaybillTrackingModal';
import { getAllOrderReferenceData } from '../../services/api/orderReference';
import styles from './ShiftDetailsPage.module.css';

const MAX_EXPORT_RECORDS = 150000;
const EXPORT_BATCH_SIZE  = 500;
const EMPTY_ITEM_FILTERS = { statusKeys: [], courierStatusLabels: [] };
const STATUS_CARD_ICONS = {
  pending: Clock,
  confirmed: CheckCircle,
  not_confirmed: XCircle,
  cancelled: XCircle,
  hold: PauseCircle,
  in_packing: Package,
  in_courier: Truck,
  settled: CheckCircle,
  returned: RotateCcw,
};


const ShiftDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, formatCurrency, formatDate } = useLocalization();
  const { error: showError } = useAlert();
  const { hasPermission } = usePermissions();

  const [shift, setShift] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [businessUsers, setBusinessUsers] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStaffRow, setSelectedStaffRow] = useState(null);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [isOrderLoading, setIsOrderLoading] = useState(false);
  const [copiedStates, setCopiedStates] = useState({});
  const [trackingOrder, setTrackingOrder] = useState(null);

  const [crossSellItems, setCrossSellItems] = useState([]);
  const [isItemsLoading, setIsItemsLoading] = useState(true);
  const [itemsPagination, setItemsPagination] = useState({
    currentPage: 1,
    pageSize: 15,
    total: 0,
    pageSizeOptions: [10, 15, 25, 50],
  });
  const [itemsSorting, setItemsSorting] = useState({ orderBy: 'createdAt', orderDirection: 'DESC' });
  const [itemsSearch, setItemsSearch] = useState('');
  const [showItemsFilters, setShowItemsFilters] = useState(false);
  const [itemsFilters, setItemsFilters] = useState(EMPTY_ITEM_FILTERS);
  const [orderStatusCounts, setOrderStatusCounts] = useState([]);
  const [courierStatusCounts, setCourierStatusCounts] = useState([]);
  const [orderStatuses, setOrderStatuses] = useState([]);

  useEffect(() => {
    getAllOrderReferenceData()
      .then((data) => setOrderStatuses(data.orderStatuses || []))
      .catch(() => setOrderStatuses([]));
  }, []);

  const loadShift = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await getShiftDetails(id);
      setShift(data.shift);
    } catch (err) {
      console.error('Error loading shift details:', err);
      showError(err.response?.data?.message || t('callCenterShifts.failedToLoadShiftDetails'));
    } finally {
      setIsLoading(false);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadShift();
  }, [loadShift]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getUsers({ status: 'true', page: 1, limit: 200 });
        setBusinessUsers(data.users || []);
      } catch (err) {
        console.error('Error loading business users:', err);
      }
    })();
  }, []);

  const buildItemsParams = () => {
    const params = {
      orderBy: itemsSorting.orderBy,
      orderDirection: itemsSorting.orderDirection,
    };
    if (Array.isArray(itemsSearch)) {
      if (itemsSearch.length > 0) params.multiSearch = itemsSearch.join(',');
    } else if (itemsSearch) {
      params.search = itemsSearch;
    }
    if (itemsFilters.statusKeys.length > 0) params.statusKeys = itemsFilters.statusKeys.join(',');
    if (itemsFilters.courierStatusLabels.length > 0) params.courierStatusLabels = itemsFilters.courierStatusLabels.join(',');
    return params;
  };

  const loadCrossSellItems = useCallback(async () => {
    try {
      setIsItemsLoading(true);
      const params = {
        page: itemsPagination.currentPage,
        pageSize: itemsPagination.pageSize,
        ...buildItemsParams(),
      };

      const data = await getShiftCrossSellItems(id, params);
      setCrossSellItems(data.items || []);
      setOrderStatusCounts(data.orderStatusCounts || []);
      setCourierStatusCounts(data.courierStatusCounts || []);
      setItemsPagination((prev) => ({ ...prev, total: data.total || 0 }));
    } catch (err) {
      console.error('Error loading cross-sold items:', err);
      showError(err.response?.data?.message || t('callCenterShifts.failedToLoadCrossSellItems'));
    } finally {
      setIsItemsLoading(false);
    }
  }, [id, itemsPagination.currentPage, itemsPagination.pageSize, itemsSorting.orderBy, itemsSorting.orderDirection, itemsSearch, itemsFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadCrossSellItems();
  }, [loadCrossSellItems]);

  const handleItemsSearchChange = (value) => {
    setItemsSearch(value);
    setItemsPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleItemsFilterChange = (newFilters) => {
    setItemsFilters({ ...EMPTY_ITEM_FILTERS, ...newFilters });
    setItemsPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const toggleStatusFilter = (statusKey) => {
    setItemsFilters((prev) => ({
      ...prev,
      statusKeys: prev.statusKeys.includes(statusKey)
        ? prev.statusKeys.filter((k) => k !== statusKey)
        : [...prev.statusKeys, statusKey],
    }));
    setItemsPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const statusCardsData = orderStatusCounts
    .filter((s) => s.count > 0)
    .map((s) => ({
      key: s.key,
      label: s.label,
      value: s.count,
      icon: STATUS_CARD_ICONS[s.key] || Clock,
      color: s.colorCode,
      bgColor: s.colorCode ? `${s.colorCode}15` : undefined,
      active: itemsFilters.statusKeys.includes(s.key),
      onClick: () => toggleStatusFilter(s.key),
    }));

  const itemsFilterDefinitions = [
    {
      key: 'statusKeys',
      label: t('callCenterShifts.orderStatusFilter'),
      type: 'custom',
      render: (value, onChange) => (
        <MultiSelectFilter
          options={orderStatuses.map((s) => ({ key: s.key, label: s.label }))}
          selectedValues={value}
          onChange={onChange}
          placeholder={t('callCenterShifts.allStatuses')}
          searchPlaceholder={t('callCenterShifts.searchStatuses')}
          valueKey="key"
          displayKey="label"
          searchKey="label"
          floatingDropdown
        />
      ),
    },
    {
      key: 'courierStatusLabels',
      label: t('callCenterShifts.courierStatusFilter'),
      type: 'custom',
      render: (value, onChange) => (
        <MultiSelectFilter
          options={courierStatusCounts.map((s) => ({ key: s.label, label: s.label }))}
          selectedValues={value}
          onChange={onChange}
          placeholder={t('callCenterShifts.allStatuses')}
          searchPlaceholder={t('callCenterShifts.searchStatuses')}
          valueKey="key"
          displayKey="label"
          searchKey="label"
          floatingDropdown
        />
      ),
    },
  ];

  const handleItemsPageSizeChange = (newSize) => {
    setItemsPagination((prev) => ({ ...prev, pageSize: newSize, currentPage: 1 }));
  };

  // ── Export ──────────────────────────────────────────────────────────────────
  const [exportModalOpen, setExportModalOpen]           = useState(false);
  const [exportStatus, setExportStatus]                 = useState('idle');
  const [exportError, setExportError]                   = useState('');
  const [exportTotalRecords, setExportTotalRecords]     = useState(0);
  const [exportFetchedRecords, setExportFetchedRecords] = useState(0);
  const exportAbortRef = useRef(false);

  const handleExport = async () => {
    setExportModalOpen(true);
    setExportStatus('running');
    setExportError('');
    setExportTotalRecords(0);
    setExportFetchedRecords(0);
    exportAbortRef.current = false;

    try {
      const filterParams = buildItemsParams();
      const firstPage    = await getShiftCrossSellItems(id, { ...filterParams, page: 1, pageSize: 1 });
      const total        = Math.min(firstPage.total || 0, MAX_EXPORT_RECORDS);

      if (total === 0) {
        setExportStatus('error');
        setExportError('No items to export with the current filters.');
        return;
      }

      setExportTotalRecords(total);
      const allItems   = [];
      const totalPages = Math.ceil(total / EXPORT_BATCH_SIZE);

      for (let page = 1; page <= totalPages; page++) {
        if (exportAbortRef.current) return;
        const data = await getShiftCrossSellItems(id, { ...filterParams, page, pageSize: EXPORT_BATCH_SIZE });
        allItems.push(...(data.items || []));
        setExportFetchedRecords(Math.min(allItems.length, total));
      }
      if (exportAbortRef.current) return;

      await exportRowsToExcel({
        sheetName: 'Cross-Sold Items',
        filename: `cc-shift-${shift?.shiftDate || id}-cross-sell-items-${new Date().toISOString().split('T')[0]}`,
        columns: [
          { header: t('ecomOrders.orderNumber'), key: 'orderNumber', width: 20 },
          { header: t('ecomOrders.airwaybillNumber'), key: 'airwaybillNumber', width: 20 },
          { header: t('callCenterCommission.staffMember'), key: 'crossSoldByName', width: 22 },
          { header: t('ecomOrders.product'), key: 'productName', width: 30 },
          { header: t('callCenterShifts.crossSellQuantity'), key: 'crossSellQuantity', width: 16 },
          { header: t('callCenterShifts.lineValue'), key: 'lineValue', width: 16, numFmt: '#,##0.00' },
          { header: t('common.status'), key: 'status', width: 18 },
          { header: t('ecomOrders.courierStatus'), key: 'courierStatus', width: 22 },
          { header: t('ecomOrders.createdBy'), key: 'createdByName', width: 22 },
          { header: t('ecomOrders.createdAt'), key: 'createdAt', width: 22 },
        ],
        rows: allItems.map((row) => ({
          orderNumber: row.orderNumber || '',
          airwaybillNumber: row.airwaybillNumber || '',
          crossSoldByName: row.crossSoldByName || '',
          productName: row.productName || '',
          crossSellQuantity: Math.round(parseFloat(row.crossSellQuantity) || 0),
          lineValue: parseFloat(row.lineValue) || 0,
          status: row.orderStatus?.label || '',
          courierStatus: row.courierStatus?.statusLabel || '',
          createdByName: row.createdByName || '',
          createdAt: row.createdAt ? formatDateTime(row.createdAt) : '',
        })),
      });

      setExportStatus('done');
    } catch (err) {
      console.error('Export error:', err);
      setExportStatus('error');
      setExportError('Failed to export items. Please try again.');
    }
  };

  const handleCloseExportModal = () => {
    exportAbortRef.current = true;
    setExportModalOpen(false);
    setExportStatus('idle');
  };

  const handleCopyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setCopiedStates(prev => ({ ...prev, [key]: false })), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      showError('Failed to copy to clipboard');
    }
  };

  const getOrderStatusIcon = (statusKey) => {
    const icons = {
      pending:       <Clock size={14} />,
      confirmed:     <CheckCircle size={14} />,
      hold:          <Clock size={14} />,
      not_confirmed: <XCircle size={14} />,
      cancelled:     <XCircle size={14} />,
      in_courier:    <Truck size={14} />,
      settled:       <CheckCircle size={14} />,
      returned:      <XCircle size={14} />
    };
    return icons[statusKey] || <Clock size={14} />;
  };

  const handleViewOrder = async (row) => {
    setIsOrderLoading(true);
    try {
      const { order } = await getEcomOrder(row.orderId || row.id);
      setSelectedOrder(order);
      setShowOrderModal(true);
    } catch (err) {
      console.error('Error loading order:', err);
      showError(err.response?.data?.message || t('ecomOrders.failedToLoadOrder'));
    } finally {
      setIsOrderLoading(false);
    }
  };

  const target = parseFloat(shift?.totalTarget) || 0;
  const achieved = parseFloat(shift?.achievedSalesValue) || 0;
  const isAchieved = shift?.achievedSalesValue != null && achieved >= target;

  const withEstimate = (content) => (
    shift?.isEstimate ? (
      <span className={styles.estimateValue}>
        {content}
        <span className={styles.estimateBadge}>{t('callCenterCommission.estimate')}</span>
      </span>
    ) : content
  );

  const staffColumns = [
    { key: 'userId', header: t('callCenterCommission.userId'), width: '90px', render: (row) => row.userId },
    { key: 'user', header: t('callCenterCommission.staffMember'), width: '240px', render: (row) => row.user?.name || '—' },
    { key: 'targetValue', header: t('callCenterShifts.targetValue'), align: 'right', render: (row) => formatCurrency(row.targetValue) },
    {
      key: 'achievedSalesValue',
      header: t('callCenterShifts.achievedSalesValue'),
      align: 'right',
      render: (row) => {
        const staffAchieved = parseFloat(row.achievedSalesValue) || 0;
        const staffTarget = parseFloat(row.targetValue) || 0;
        const staffIsAchieved = staffAchieved >= staffTarget;
        return (
          <span className={staffIsAchieved ? styles.achieved : styles.notAchieved}>
            {formatCurrency(row.achievedSalesValue)}
          </span>
        );
      },
    },
    { key: 'commissionValue', header: t('callCenterCommission.commissionAmount'), align: 'right', render: (row) => withEstimate(formatCurrency(row.commissionValue)) },
  ];

  const staffActions = {
    render: (row) => (
      <button
        className={styles.actionIconButton}
        title={t('common.view')}
        onClick={(e) => { e.stopPropagation(); setSelectedStaffRow(row); }}
      >
        <Eye size={14} />
      </button>
    ),
    width: '70px',
    frozen: true,
  };

  const slabColumns = [
    { key: 'minValue', header: t('callCenterShifts.minValue'), render: (row) => row.minValue },
    {
      key: 'maxValue',
      header: t('callCenterShifts.maxValue'),
      render: (row) => (row.maxValue != null ? row.maxValue : t('callCenterShifts.openEnded')),
    },
    { key: 'rate', header: t('callCenterShifts.amountPerUnit'), render: (row) => `${row.rate}%` },
    { key: 'slabQty', header: t('callCenterShifts.slabQty'), render: (row) => row.slabQty },
    { key: 'slabTotal', header: t('callCenterShifts.slabTotal'), render: (row) => formatCurrency(row.slabTotal) },
  ];

  const itemColumns = [
    {
      key: 'orderNumber',
      header: t('ecomOrders.orderNumber'),
      sortable: true,
      width: '140px',
      render: (row) => {
        if (!row.orderNumber) return <span className={styles.naText}>—</span>;
        const copyKey  = `order-${row.id}`;
        const isCopied = copiedStates[copyKey];

        return (
          <div className={styles.orderNumberCell}>
            <div className={styles.orderNumber} onClick={(e) => { e.stopPropagation(); handleViewOrder(row); }}>
              #{row.orderNumber}
            </div>
            <button className={styles.copyButton} onClick={(e) => { e.stopPropagation(); handleCopyToClipboard(row.orderNumber, copyKey); }} title={isCopied ? 'Copied!' : 'Copy order number'}>
              {isCopied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        );
      },
    },
    {
      key: 'airwaybillNumber',
      header: t('ecomOrders.airwaybillNumber'),
      width: '160px',
      render: (row) => {
        if (!row.airwaybillNumber) return <span className={styles.naText}>{t('common.na')}</span>;
        const copyKey  = `awb-${row.id}`;
        const isCopied = copiedStates[copyKey];

        return (
          <div className={styles.courierInfo}>
            <div className={styles.airwaybillWrapper}>
              <button className={styles.airwaybillNumber} onClick={(e) => { e.stopPropagation(); setTrackingOrder(row); }} title={t('ecomOrders.viewPackageTimeline')}>
                {row.airwaybillNumber}
              </button>
              <button className={styles.copyButton} onClick={(e) => { e.stopPropagation(); handleCopyToClipboard(row.airwaybillNumber, copyKey); }} title={isCopied ? 'Copied!' : 'Copy airwaybill number'}>
                {isCopied ? <Check size={12} /> : <Copy size={12} />}
              </button>
            </div>
          </div>
        );
      },
    },
    {
      key: 'crossSoldByName',
      header: t('callCenterCommission.staffMember'),
      render: (row) => (
        <div className={styles.createdByInfo}><User size={12} />{row.crossSoldByName || <span className={styles.naText}>—</span>}</div>
      ),
    },
    {
      key: 'productName',
      header: t('ecomOrders.product'),
      render: (row) => row.productName
        ? <div className={styles.productItem}>{row.productName}</div>
        : <span className={styles.naText}>—</span>,
    },
    {
      key: 'crossSellQuantity',
      header: t('callCenterShifts.crossSellQuantity'),
      width: '150px',
      render: (row) => Math.round(parseFloat(row.crossSellQuantity) || 0),
    },
    {
      key: 'lineValue',
      header: t('callCenterShifts.lineValue'),
      sortable: true,
      width: '150px',
      render: (row) => formatCurrency(row.lineValue),
    },
    {
      key: 'status',
      header: t('common.status'),
      width: '150px',
      render: (row) => (
        <div className={styles.statusCell}>
          <div
            className={styles.statusBadge}
            style={{
              backgroundColor: row.orderStatus?.colorCode ? `${row.orderStatus.colorCode}20` : '#F59E0B20',
              borderColor: row.orderStatus?.colorCode || '#F59E0B',
              color: row.orderStatus?.colorCode || '#92400e',
            }}
          >
            {getOrderStatusIcon(row.orderStatus?.key)}
            <span>{row.orderStatus?.label || '—'}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'courierStatus',
      header: t('ecomOrders.courierStatus'),
      width: '180px',
      render: (row) => {
        const label = row.courierStatus?.statusLabel;
        if (!label) return <span className={styles.naText}>{t('common.na')}</span>;
        const colorCode = row.courierStatus?.colorCode;
        return (
          <div
            className={styles.courierStatusBadge}
            title={label}
            style={colorCode ? { backgroundColor: `${colorCode}20`, borderColor: colorCode, color: colorCode } : undefined}
          >
            <span className={styles.courierStatusText}>{label}</span>
          </div>
        );
      },
    },
    {
      key: 'createdByName',
      header: t('ecomOrders.createdBy'),
      width: '150px',
      render: (row) => (
        <div className={styles.createdByInfo}><User size={12} />{row.createdByName || <span className={styles.naText}>{t('common.na')}</span>}</div>
      ),
    },
    {
      key: 'createdAt',
      header: t('ecomOrders.createdAt'),
      sortable: true,
      width: '150px',
      render: (row) => (
        <div className={styles.dateCell}>
          <div className={styles.dateInfo}><Calendar size={12} />{formatOrderDate(row.createdAt)}</div>
          <div className={styles.timeInfo}>{formatTime(row.createdAt)}</div>
        </div>
      ),
    },
  ];

  const itemActions = {
    render: (row) => (
      <div className={styles.actionButtons}>
        {hasPermission('view_ecom_order') && (
          <Link
            to={`/orders/${row.orderId || row.id}`}
            className={styles.actionIconButton}
            title={t('common.view')}
            onClick={(e) => e.stopPropagation()}
          >
            <Eye size={14} />
          </Link>
        )}
        {hasPermission('edit_ecom_order') && (
          <Link
            to={`/orders/${row.orderId || row.id}/edit`}
            className={styles.actionIconButton}
            title={t('common.edit')}
            onClick={(e) => e.stopPropagation()}
          >
            <Edit size={14} />
          </Link>
        )}
      </div>
    ),
    width: '90px',
    frozen: true,
  };

  return (
    <div className={styles.shiftDetailsPage}>
      <PageHeader
        title={shift ? `${t('callCenterShifts.detailsPageTitle')} — ${formatDate(shift.shiftDate)}` : t('callCenterShifts.detailsPageTitle')}
        description={t('callCenterShifts.detailsPageDescription')}
        icon={Phone}
        actions={
          <div className={styles.pageActions}>
            {hasPermission('manage_callcenter_shifts') && shift && (
              <button onClick={() => setShowEditModal(true)} className={styles.editButton}>
                <Edit size={18} />
                {t('callCenterShifts.edit')}
              </button>
            )}
            <button onClick={() => navigate('/call-center/shifts')} className={styles.backButton}>
              <ArrowLeft size={18} />
              {t('common.back')}
            </button>
          </div>
        }
      />

      {shift && (
        <>
          <div className={styles.summaryCards}>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>{t('callCenterShifts.totalTarget')}</span>
              <span className={styles.summaryValue}>{formatCurrency(target)}</span>
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>{t('callCenterShifts.achievedSalesValue')}</span>
              <span className={`${styles.summaryValue} ${isAchieved ? styles.achieved : styles.notAchieved}`}>
                {withEstimate(formatCurrency(achieved))}
              </span>
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>{t('callCenterCommission.totalCommission')}</span>
              <span className={styles.summaryValue}>{withEstimate(formatCurrency(shift.totalCommission))}</span>
            </div>
          </div>

          <h3 className={styles.sectionTitle}>{t('callCenterShifts.staffCommissionBreakdown')}</h3>
          <DataTable
            data={shift.staffBreakdown || []}
            columns={staffColumns}
            actions={staffActions}
            loading={isLoading}
            emptyMessage={t('callCenterCommission.noStaffCommissionRecords')}
          />

          <h3 className={styles.sectionTitle}>{t('callCenterShifts.crossSoldItems')}</h3>
          {statusCardsData.length > 0 && (
            <div className={styles.statusCards}>
              <StatsCards
                stats={statusCardsData}
                clickable
                columns={Math.min(statusCardsData.length, 5)}
                minColumnWidth="140px"
                gap="0.75rem"
                valueFontSize="1.25rem"
              />
            </div>
          )}
          <FilterPanel
            filters={itemsFilterDefinitions}
            values={itemsFilters}
            onChange={handleItemsFilterChange}
            onClear={() => {
              setItemsSearch('');
              setItemsFilters(EMPTY_ITEM_FILTERS);
              setItemsPagination((prev) => ({ ...prev, currentPage: 1 }));
            }}
            show={showItemsFilters}
            onToggle={() => setShowItemsFilters(!showItemsFilters)}
            onExport={handleExport}
            showExport={true}
            exportButtonText={t('common.export')}
            searchConfig={{
              show: true,
              value: itemsSearch,
              onChange: handleItemsSearchChange,
              placeholder: t('callCenterShifts.searchCrossSoldItems'),
              multiSearch: true,
            }}
            pageSizeConfig={{
              show: true,
              value: itemsPagination.pageSize,
              onChange: handleItemsPageSizeChange,
              options: itemsPagination.pageSizeOptions,
            }}
          />
          <DataTable
            data={crossSellItems}
            columns={itemColumns}
            actions={itemActions}
            onRowClick={handleViewOrder}
            sorting={itemsSorting}
            onSortChange={setItemsSorting}
            pagination={itemsPagination}
            onPaginationChange={setItemsPagination}
            loading={isItemsLoading || isOrderLoading}
            emptyMessage={t('callCenterShifts.noCrossSellItemsForShift')}
          />
        </>
      )}

      {showEditModal && shift && (
        <CallCenterShiftModal
          mode="edit"
          shift={shift}
          businessUsers={businessUsers}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            loadShift();
          }}
        />
      )}

      {selectedStaffRow && (
        <StaffSlabBreakdownModal
          staffRow={selectedStaffRow}
          onClose={() => setSelectedStaffRow(null)}
        />
      )}

      {showOrderModal && selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => { setShowOrderModal(false); setSelectedOrder(null); }}
          onUpdate={(updated) => setSelectedOrder(updated)}
        />
      )}

      {trackingOrder && (
        <AirwaybillTrackingModal
          orderId={trackingOrder.orderId}
          airwaybillNumber={trackingOrder.airwaybillNumber}
          onClose={() => setTrackingOrder(null)}
        />
      )}

      <ExportProgressModal
        isOpen={exportModalOpen}
        onClose={handleCloseExportModal}
        status={exportStatus}
        error={exportError}
        totalRecords={exportTotalRecords}
        fetchedRecords={exportFetchedRecords}
        title="Exporting Cross-Sold Items"
      />
    </div>
  );
};

export default ShiftDetailsPage;
